import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize, RotateCcw } from "lucide-react";

const MIN = 1, MAX = 8;
const RESET = { s: 1, x: 0, y: 0 };

// Fit-to-screen image: wheel zooms at cursor, middle-drag (or left-drag when zoomed) pans, double-click resets.
export default function ZoomImage({ src, alt = "" }) {
  const box = useRef(null);
  const drag = useRef(null);
  const [t, setT] = useState(RESET);
  const [panning, setPanning] = useState(false);

  useEffect(() => setT(RESET), [src]);

  const zoomAt = (factor, cx = 0, cy = 0) => setT((p) => {
    const s = Math.min(MAX, Math.max(MIN, p.s * factor));
    if (s === MIN) return RESET;
    const k = s / p.s;
    return { s, x: cx - (cx - p.x) * k, y: cy - (cy - p.y) * k };
  });

  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left - r.width / 2, e.clientY - r.top - r.height / 2);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const down = (e) => {
    if (e.button !== 1 && !(e.button === 0 && t.s > 1)) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, x: t.x, y: t.y };
    setPanning(true);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const r = box.current.getBoundingClientRect();
    setT((p) => {
      const mx = ((p.s - 1) * r.width) / 2 + r.width / 4, my = ((p.s - 1) * r.height) / 2 + r.height / 4;
      const clamp = (v, m) => Math.max(-m, Math.min(m, v));
      return { ...p, x: clamp(d.x + e.clientX - d.sx, mx), y: clamp(d.y + e.clientY - d.sy, my) };
    });
  };
  const up = () => { drag.current = null; setPanning(false); };
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : box.current?.requestFullscreen?.())?.catch?.(() => {});

  return (
    <div ref={box} data-testid="zoom-viewer"
      className={`absolute inset-0 overflow-hidden bg-black select-none ${panning ? "cursor-grabbing" : t.s > 1 ? "cursor-grab" : "cursor-zoom-in"}`}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      onMouseDown={(e) => e.button === 1 && e.preventDefault()} onAuxClick={(e) => e.preventDefault()}
      onDoubleClick={() => setT(RESET)}>
      <img src={src} alt={alt} draggable={false} data-testid="zoom-image"
        className="w-full h-full object-contain pointer-events-none"
        style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})`, transition: panning ? "none" : "transform 80ms ease-out" }} />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/70 border border-zinc-800 rounded-full px-1.5 py-1 backdrop-blur"
        onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => zoomAt(1 / 1.25)} className="ax-icon-btn" title="Zoom out" data-testid="zoom-out"><ZoomOut className="w-3.5 h-3.5" /></button>
        <span className="text-[11px] font-mono text-zinc-300 w-12 text-center" data-testid="zoom-level">{Math.round(t.s * 100)}%</span>
        <button type="button" onClick={() => zoomAt(1.25)} className="ax-icon-btn" title="Zoom in" data-testid="zoom-in"><ZoomIn className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => setT(RESET)} className="ax-icon-btn" title="Fit to screen" data-testid="zoom-reset"><RotateCcw className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={fullscreen} className="ax-icon-btn" title="Fullscreen" data-testid="zoom-fullscreen"><Maximize className="w-3.5 h-3.5" /></button>
      </div>
      <div className="absolute top-3 left-3 text-[10px] font-mono text-zinc-500 bg-black/60 rounded px-2 py-1 pointer-events-none">
        Scroll to zoom · middle-drag or drag to pan · double-click to fit
      </div>
    </div>
  );
}
